import { NgForOf, NgIf } from '@angular/common';
import { Component, effect, input, InputSignal, OnInit, output, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatStep, MatStepContent, MatStepLabel, MatStepper, MatStepperIcon } from '@angular/material/stepper';
import { FormioForm } from '@formio/angular';
import { TranslateModule } from '@ngx-translate/core';
import { get } from 'lodash';
import { filter } from 'rxjs/operators';
import { EDialogWidth } from '../../../../../core/constants/ui.constants';
import { TrimmedRequiredValidator } from '../../../../../core/validators/trimmed-required.validator';
import {
  ETableCrudScope,
  IDatatableColumn,
  IDatatableConfiguration,
} from '../../../../../shared/components/datatable/datatable.interface';
import { ErrorMessagesComponent } from '../../../../../shared/components/error-messages/error-messages.component';
import { IBaseDatatableState } from '../../../../../state/base-datatable-store.service';
import { ELoadStatus } from '../../../../../state/state.interface';
import { FormUtilitiesService } from '../../../../../utilities/form-utilities.service';
import { HttpUtilitiesService } from '../../../../../utilities/http-utilities.service';
import { ESnackbar } from '../../../../../utilities/snackbar.service';
import { BaseCrudPageComponent } from '../../../../app/base-crud-page.component';
import { EWorkInstructionEditorSidebar, IWorkInstructionEditor } from '../../work-instructions.interface';
import {
  IWorkInstructionTemplate,
  TAddWorkInstructionTemplate,
  TFormControlWorkInstructionTemplate,
  TFormWorkInstructionTemplateComponent,
} from './work-instruction-templates.interface';
import { WorkInstructionTemplatesStore } from './work-instruction-templates.store';

@Component({
  imports: [
    ErrorMessagesComponent,
    MatButton,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatError,
    MatFormField,
    MatInput,
    MatLabel,
    NgIf,
    ReactiveFormsModule,
    TranslateModule,
    MatStepper,
    MatStep,
    MatStepLabel,
    NgForOf,
    MatStepContent,
    MatStepperIcon,
    MatIcon,
  ],
  providers: [WorkInstructionTemplatesStore, HttpUtilitiesService],
  selector: 'app-work-instruction-templates',
  standalone: true,
  templateUrl: './work-instruction-templates.component.html',
})
export class WorkInstructionTemplatesComponent
  extends BaseCrudPageComponent<IWorkInstructionTemplate, IBaseDatatableState<IWorkInstructionTemplate>>
  implements OnInit
{
  @ViewChild('addEditFormDialog') addEditFormDialog!: TemplateRef<HTMLElement>;
  @ViewChild('stepper') stepper: MatStepper | undefined;

  public workInstruction: InputSignal<IWorkInstructionEditor> = input.required();
  public readonly editedFormOfSelectedItem = input<FormioForm>();
  public readonly drawerType = input<EWorkInstructionEditorSidebar>();
  public applySelectedTemplate = output<IWorkInstructionTemplate>();

  public datatableConfigurations: IDatatableConfiguration<IWorkInstructionTemplate> = {
    addButtonText: this.addText,
    addScope: ETableCrudScope.single,
    dataRefreshEvent: this.setPageToOneAndRefreshDatatable$,
    deleteScope: ETableCrudScope.single,
    editScope: ETableCrudScope.single,
  };

  public readonly datatableColumns: IDatatableColumn<IWorkInstructionTemplate>[] = [
    { id: 'id', isSortable: false },
    { id: 'name', isSortable: true },
  ];

  private dialogRef: MatDialogRef<HTMLElement> | undefined;

  constructor(
    public readonly store: WorkInstructionTemplatesStore,
    public readonly formService: FormUtilitiesService,
  ) {
    super(store, {
      ambiguousNumberContext: 'field.ambiguousNumberOfWorkInstructionTemplate',
      multiContext: 'field.workInstructionTemplates',
      nameProperty: 'name',
      singleContext: 'field.workInstructionTemplate',
    });

    effect(() => {
      if (this.drawerType() === EWorkInstructionEditorSidebar.templates) {
        this.getItems();
      }
    });
  }

  public readonly addEditForm: FormGroup<TFormControlWorkInstructionTemplate> = new FormGroup({
    name: new FormControl('', [
      Validators.required,
      TrimmedRequiredValidator(),
      Validators.minLength(1),
      Validators.maxLength(50),
    ]),
  });

  public ngOnInit(): void {
    this.getItems();

    this.subscriptions.push(
      this.store
        .select((state: IBaseDatatableState<IWorkInstructionTemplate>) => state.singleCrudLoading)
        .pipe(filter((value: ELoadStatus) => value === ELoadStatus.success))
        .subscribe(() => {
          this.snackbarService.open(this.translate.instant('system.message.changesSuccessfully'), ESnackbar.success);
          this.getItems();
          this.dialogRef?.close();
        }),
      this.store
        .select((state: IBaseDatatableState<IWorkInstructionTemplate>) => state.dataLoading)
        .pipe(filter((value: ELoadStatus) => value === ELoadStatus.success))
        .subscribe(() => {
          const [selectedItem] = this.selectedRows();
          const selectedStepId: number = selectedItem ? selectedItem.id : get(this.store.data$(), '0.id');

          if (selectedStepId) {
            this.onChangeStep(selectedStepId);
          }
        }),
    );
  }

  public initializeAdd(): void {
    this.setupForAdd();
    this.addEditForm.enable();

    this.addEditForm.reset({
      name: '',
    });

    this.dialogRef = this.matDialog.open(this.addEditFormDialog, {
      disableClose: true,
      restoreFocus: false,
      width: EDialogWidth.xSmall,
    });
  }

  public initializeEdit(): void {
    if (!this.selectedRows().length) {
      return;
    }

    this.addEditForm.enable();
    this.setupForEdit();

    if (this.isBulkEdit()) {
      this.addEditForm.disable();
      this.addEditForm.reset();
    } else {
      const [item] = this.selectedRows();

      this.addEditForm.reset({
        name: item.name,
      });
    }

    this.dialogRef = this.matDialog.open(this.addEditFormDialog, {
      disableClose: true,
      restoreFocus: false,
      width: EDialogWidth.xSmall,
    });
  }

  public onFormDialogSubmit(): void {
    if (!this.addEditForm.valid) {
      return;
    }

    if (!this.isEditDialog()) {
      this.store.createOne(this.formatOnePayload(this.addEditForm.value as IWorkInstructionTemplate));
    } else {
      const [item] = this.selectedRows();
      this.store.updateOne({
        dto: this.formatOnePayload(this.addEditForm.value as IWorkInstructionTemplate),
        id: item.id,
      });
    }
  }

  private getItems(): void {
    this.store.loadWorkInstructionTemplates({
      fields: ['name', 'form'],
      limit: 1000,
      page: 1,
      sort: [{ active: 'id', direction: 'desc' }],
    });
  }

  private formatOnePayload(item: TFormWorkInstructionTemplateComponent): TAddWorkInstructionTemplate {
    return {
      name: FormUtilitiesService.trimIfString(item.name),
    };
  }

  public onChangeStep(id?: number): void {
    const itemId: number = id ?? Number(this.stepper?.selected?.label);

    if (!itemId) {
      return;
    }

    this.selectedDatatableItems.set([itemId]);
  }

  public onClickedApply(): void {
    const [item] = this.selectedRows();

    this.applySelectedTemplate.emit(item);
  }
}
